import { useLoad } from "@redotech/react-util/load";
import { CustomerCustomFieldDataType } from "@redotech/redo-model/customer-custom-fields/customer-custom-fields-definition";
import { TimeframeConditionType } from "@redotech/redo-model/marketing/segments/segment-timeframe";
import { CustomerCharacteristicType } from "@redotech/redo-model/marketing/segments/segment-types";
import {
  ComparisonType,
  NumericCompareOperator,
  TokenCompareOperators,
} from "@redotech/redo-model/segments/segment-where-condition";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../flex";
import { SegmentBuilderText } from "../segment-builder-text";
import { CustomFieldConditionInProgress } from "../segment-in-progress";
import { BooleanFilter } from "./boolean-filter";
import { FullDateTimeframeFilter } from "./full-date-timeframe-filter";
import { NumericFilter } from "./numeric-filter";
import {
  SegmentTokenKey,
  SegmentTokenOptionsContext,
  TokenFilter,
} from "./token-filter";

function comparisonForDataType(
  typeHint?: CustomerCustomFieldDataType,
): CustomFieldConditionInProgress<any>["comparison"] {
  switch (typeHint) {
    case CustomerCustomFieldDataType.STRING:
    case undefined:
      return {
        type: ComparisonType.TOKEN,
        operator: TokenCompareOperators.ANY,
        values: [],
      };
    case CustomerCustomFieldDataType.NUMBER:
      return {
        type: ComparisonType.NUMERIC,
        operator: NumericCompareOperator.EQ,
      };
    case CustomerCustomFieldDataType.BOOLEAN:
      return { type: ComparisonType.BOOLEAN, value: true };
    case CustomerCustomFieldDataType.DATE:
      return { type: TimeframeConditionType.BEFORE };
    default:
      assertNever(typeHint);
  }
}

/**
 * Custom field filter for editing custom field where conditions
 */
export const CustomFieldFilter = memo(function CustomFieldFilter({
  condition,
  setCondition,
}: {
  condition: CustomFieldConditionInProgress<any>;
  setCondition: (condition: CustomFieldConditionInProgress<any>) => void;
}) {
  const tokenOptionsFetcher = useContext(SegmentTokenOptionsContext);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);

  const customFieldsLoad = useLoad(
    async () =>
      tokenOptionsFetcher.getTokenOptions(
        new SegmentTokenKey({
          dataStructure: "customer-characteristic",
          dataStructureField: CustomerCharacteristicType.CUSTOM_FIELDS,
        }),
        { search: debouncedSearch },
      ),
    [tokenOptionsFetcher, debouncedSearch],
  );

  const customFieldOptions: RedoListItem<string>[] = useMemo(() => {
    return (
      (customFieldsLoad.value ?? [])[0]?.map<RedoListItem<string>>((item) => {
        return { id: item.id, type: "text", value: item.id, text: item.text };
      }) ?? []
    );
  }, [customFieldsLoad.value]);

  const selectedCustomField: RedoListItem<string> | undefined = useMemo(() => {
    if (!condition.declarationId) return undefined;

    const matchingField = customFieldOptions.find(
      (option) => option.id === condition.declarationId,
    );

    if (matchingField) return matchingField;

    return {
      id: condition.declarationId,
      type: "text",
      value: condition.declarationId,
      text: condition.declarationId,
    };
  }, [condition.declarationId, customFieldOptions]);

  const handleSelectCustomField = (
    id: string,
    typeHint?: CustomerCustomFieldDataType,
  ) => {
    const newComparison = comparisonForDataType(typeHint);
    setCondition({
      ...condition,
      declarationId: id,
      comparison: newComparison,
    });
  };

  const renderFilterComponent = () => {
    if (!condition.declarationId) return null;

    switch (condition.comparison.type) {
      case ComparisonType.TOKEN:
        return (
          <TokenFilter
            comparison={condition.comparison}
            dataStructure="customer-characteristic"
            dimension={CustomerCharacteristicType.CUSTOM_FIELDS}
            precedingValues={[condition.declarationId]}
            setComparison={(newComparison) => {
              setCondition({ ...condition, comparison: newComparison });
            }}
          />
        );
      case ComparisonType.NUMERIC:
        return (
          <NumericFilter
            comparison={condition.comparison}
            setComparison={(newComparison) => {
              setCondition({ ...condition, comparison: newComparison });
            }}
          />
        );
      case ComparisonType.BOOLEAN:
        return (
          <BooleanFilter
            comparison={condition.comparison}
            setComparison={(newComparison) => {
              setCondition({ ...condition, comparison: newComparison });
            }}
          />
        );
      case undefined:
        return null;
      // Date custom fields use timeframe conditions (e.g. before, after, between)
      default:
        return (
          <FullDateTimeframeFilter
            comparison={condition.comparison}
            setComparison={(newComparison) => {
              setCondition({ ...condition, comparison: newComparison });
            }}
          />
        );
    }
  };

  return (
    <Flex align="center" gap="sm" wrap="wrap">
      <SegmentBuilderText>is</SegmentBuilderText>

      {/* Custom Field Declaration Selector */}
      <RedoSingleSelectDropdownInput
        fitToAnchor={false}
        flexGrow={false}
        itemsLoading={customFieldsLoad.pending}
        options={customFieldOptions}
        optionSelected={({ item }) => {
          if (item.value) {
            const typeHint = (customFieldsLoad.value ?? [])[0]?.find(
              (opt) => opt.id === item.value,
            )?.typeHint;
            handleSelectCustomField(item.value, typeHint);
          }
        }}
        placeholder="Select custom field"
        searchPlaceholder="Search"
        searchString={search}
        selectedItem={selectedCustomField}
        setSearchString={setSearch}
        size="xs"
      />
      {renderFilterComponent()}
    </Flex>
  );
});
